import React, {Fragment, useContext, useEffect} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {ApiUtility} from "../common/ApiUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ListPaginationOptions} from "../common/ListPaginationOptions";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {CommonListPageHeader} from "../common/CommonListPageHeader";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {UrlUtility} from "../common/UrlUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {PillButton} from "../common/PillButton";
import {SubscriptionDataUtility} from "../common/SubscriptionDataUtility";
import {InvoiceDataUtility} from "../common/InvoiceDataUtility";
import {SingleInvoice} from "../invoices/SingleInvoice";
import {ConfirmCancelModal} from "../common/ConfirmCancelModal";
import {Invoice} from "../invoices/InvoiceTypes";
import {SelectUtility} from "../common/SelectUtility";
import {SelectOption} from "../common/CommonUIElementTypes";
import Select, {ActionMeta, SingleValue } from 'react-select';
import {Subscription} from "./SubscriptionTypes";
import {Constants} from "../common/Constants";

interface SubscriptionInvoicesListPageURLQueryParams {
    page?: number;
    searchText?: string;
    stripeCustomerId?: string;
}

interface SubscriptionInvoicesListPageRouteParams {
    invoiceId: string | undefined;
}

export interface SubscriptionInvoicesListPageProps extends RouteComponentProps<SubscriptionInvoicesListPageRouteParams> {

}

interface SubscriptionInvoicesListPageState {
    selectedPage: number;
    searchText: string;
    filterStripeCustomerId?: string;
    showUpdateInvoiceModal: boolean;
    showConfirmSyncModal: boolean;
    updateInvoiceId?: number;
    invoiceList: Invoice[];
    pageCount: number;
    loading: boolean;
    prevUrlQueryString?: string;
    prevParams?: SubscriptionInvoicesListPageRouteParams;
    stripeCustomerOptions: SelectOption[];
}

export const SubscriptionInvoicesListPage = function (props: SubscriptionInvoicesListPageProps) {
    const context = useContext(GlobalContext);
    const urlBase = '/subscription-invoices';
    const initialState: SubscriptionInvoicesListPageState = {
        selectedPage: 1,
        searchText: '',
        filterStripeCustomerId: undefined,
        showUpdateInvoiceModal: false,
        showConfirmSyncModal: false,
        updateInvoiceId: undefined,
        invoiceList: [],
        pageCount: 0,
        loading: false,
        prevUrlQueryString: undefined,
        prevParams: undefined,
        stripeCustomerOptions: []
    }
    const renderableData = useRenderableData(initialState)

    // Update state if URL changes.
    if (
        (props.location?.search) !== renderableData.prevUrlQueryString ||
        (props.match?.params) !== renderableData.prevParams
    ) {
        let selectedPage = 1;
        let searchText = '';
        let filterStripeCustomerId = undefined;
        let updateInvoiceId = null;
        let showUpdateInvoiceModal = false;

        let routeParams = props.match && props.match.params;
        if (routeParams?.invoiceId) {
            showUpdateInvoiceModal = true;
            updateInvoiceId = routeParams.invoiceId;
        }

        let queryParams: SubscriptionInvoicesListPageURLQueryParams = UrlUtility.getQueryParamsFromProps(props);
        if (queryParams.page) {
            selectedPage = queryParams.page;
        }
        if (queryParams.searchText) {
            searchText = queryParams.searchText;
        }
        if (queryParams.stripeCustomerId) {
            filterStripeCustomerId = queryParams.stripeCustomerId;
        }

        renderableData.updateAndRender({
            selectedPage: selectedPage,
            searchText: searchText,
            filterStripeCustomerId: filterStripeCustomerId,

            showUpdateInvoiceModal: showUpdateInvoiceModal || false,
            updateInvoiceId: updateInvoiceId,

            prevUrlQueryString: props.location?.search,
            prevParams: props.match && props.match.params,
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadList();
        },
        [
            '' + renderableData.selectedPage,
            '' + renderableData.searchText,
            '' + renderableData.filterStripeCustomerId
        ]
    );

    function reloadList(callback?: Function) {
        let renderableDataUpdate: Partial<SubscriptionInvoicesListPageState> = {};
        renderableData.updateAndRender({loading: true})
        ApiUtility.getSubscriptionInvoicesList(
            renderableData.selectedPage,
            renderableData.searchText,
            renderableData.filterStripeCustomerId
        )
            .then(function (listData) {
                renderableDataUpdate = {
                    ...listData,
                    loading: false
                }

                return new Promise<void>(function (resolve, reject) {
                    if (renderableData.stripeCustomerOptions.length === 0) {
                        return ApiUtility.getStripeCustomersList()
                            .then(function (stripeCustomersList) {
                                renderableDataUpdate.stripeCustomerOptions = SelectUtility.modelArrayToSelectOptions(
                                    stripeCustomersList, 'id', 'name'
                                )
                                resolve()
                            })
                    } else {
                        resolve()
                    }
                });
            })
            .then(function () {
                renderableData.updateAndRender(renderableDataUpdate);
                if (callback) {
                    callback();
                }
            })
            .catch(function (error) {
                renderableData.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the subscription invoice list.`);
            });
    }

    function goToReadSubscriptionModal(subscriptionId: number) {
        context.navigator.navigateTo(`/subscriptions/${subscriptionId}${props.location.search}`);
    }

    function goToUpdateInvoiceModal(invoiceId: number) {
        context.navigator.navigateTo(`${urlBase}/${invoiceId}${props.location.search}`);
    }

    function goToSubscriptionsListPage() {
        context.navigator.navigateTo("/subscriptions");
    }

    function closeModals() {
        context.navigator.navigateTo(`${urlBase}${props.location.search}`);
    }

    function afterSuccessfulInvoiceSave(invoiceId: number) {
        closeModals()
        reloadList(
            () => goToUpdateInvoiceModal(invoiceId)
        );
    }

    function afterSuccessfulInvoiceCancel() {
        closeModals()
        reloadList()
    }

    function filterByStripeCustomerId(option: SelectOption) {
        if (renderableData.filterStripeCustomerId !== option?.value) {
            let queryParams: SubscriptionInvoicesListPageURLQueryParams = UrlUtility.getQueryParamsFromProps(props);
            queryParams.stripeCustomerId = option?.value as string || undefined;
            queryParams.page = 1;
            let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(queryParams);
            context.navigator.navigateTo(`${urlBase}${queryString}`);
        }
    }

    function onClickSync() {
        renderableData.updateAndRender({showConfirmSyncModal: true})
    }

    function closeConfirmSyncModal() {
        renderableData.updateAndRender({showConfirmSyncModal: false})
    }

    function performSync() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.syncAllSubscriptionInvoices()
            .then((response) => {
                closeConfirmSyncModal();
                MessageDisplayerUtility.success(`Successfully synced subscription invoices.`);
                reloadList();
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while syncing the subscription invoices.`);
            });
    }

    function renderInvoicesListDisplay(invoicesList: Invoice[]) {
        let invoicesListDisplay = [];

        for (let i = 0; i < invoicesList.length; i++) {
            const invoice = invoicesList[i];
            const subscription = invoice.subscription as Subscription;
            let description = subscription.description || '';
            if (description.length > 200) {
                description = description.substring(0, 200) + '...';
            }

            let totalDisplay = SubscriptionDataUtility.getSubscriptionTotalDisplay(subscription)
            let linesDisplay = SubscriptionDataUtility.getSubscriptionLinesDisplay(subscription)
            let invoiceStatusClass = InvoiceDataUtility.invoiceStatusToClass(invoice.invoice_status)
            let invoiceTitle = SubscriptionDataUtility.getSubscriptionInvoiceTitle(subscription, invoice);

            invoicesListDisplay.push(
                <div
                    key={i + 1}
                    id={`subscriptionInvoiceListRow-${invoice.id}`}
                    className={`common-list-row common-list-values-row subscription-invoices-list-row for-subscription-${subscription.id}`}
                    onClick={() => goToUpdateInvoiceModal(invoice.id)}
                >
                    <div className="common-list-row-content">
                        <div
                            className="common-list-row-cell common-list-row-value-cell subscription-invoices-list-row-cell basics"
                        >
                            <div>
                                <div>
                                    <span>
                                        <span className="invoice-title">
                                            {invoiceTitle}
                                        </span>
                                        <span
                                            className={
                                                `invoice-customer ext-stripe-id-${subscription.ext_stripe_customer_id}`
                                            }
                                        >
                                            {`${subscription.ext_stripe_customer_name}`}
                                        </span>
                                    </span>
                                </div>
                                <div>
                                    <span className="invoice-subscription-basics">
                                        <span className="subscription-invoice-total-display">{totalDisplay}</span>
                                        <span className="subscription-invoice-lines-summary">{linesDisplay}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell subscription-invoices-list-row-cell subscription-description"
                        >{description}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell subscription-invoices-list-row-cell status"
                        >
                            <div className="invoice-status-content">
                                <div className={`invoice-status-badge ${invoiceStatusClass}`}>
                                    {invoice.invoice_status.name}
                                </div>
                                <div className="invoice-status-additional-info">
                                    {invoice.invoice_status.additional_info}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return invoicesListDisplay;
    }

    if (renderableData.invoiceList) {
        return (
            <div className="common-list-page subscription-invoices-list-page">
                <CommonListPageHeader
                    tabs={[
                        {
                            title: Constants.subscriptionPagesTabs.subscriptions,
                            onClick: goToSubscriptionsListPage,
                            selected: false
                        },
                        {
                            title: Constants.subscriptionPagesTabs.subscriptionInvoices,
                            onClick: () => null,
                            selected: true
                        },
                    ]}
                    rightHeaderRenderer={() => (
                        <Fragment>
                            <Select
                                placeholder="Filter by customer"
                                className="common-form-select header-filter-select customer-filter-select"
                                classNamePrefix="common-form-select"
                                value={SelectUtility.selectOptionForId(
                                    '' + renderableData.filterStripeCustomerId,
                                    renderableData.stripeCustomerOptions
                                )}
                                onChange={(newValue: SingleValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) => filterByStripeCustomerId(newValue as SelectOption)}
                                options={renderableData.stripeCustomerOptions}
                                isClearable={true}
                            />
                            <PillButton
                                onClick={onClickSync}
                                containerClasses="sync-button-container"
                                buttonClasses="sync-button"
                                leftIconSrc={"/static/images/sync-white.svg"}
                                buttonText="SYNC ALL"
                            />
                        </Fragment>
                    )}
                />

                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={renderableData.loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (renderableData.invoiceList.length > 0) {
                            return (
                                <Fragment>
                                    <div className="common-list-header">
                                        <div className="common-list-row common-list-labels-row">
                                            <div className="common-list-row-content">
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscription-invoices-list-row-cell basics"
                                                >Basics
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscription-invoices-list-row-cell subscription-description"
                                                >Subscription Description
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscription-invoices-list-row-cell status"
                                                >Status
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-list-content">
                                        {renderInvoicesListDisplay(renderableData.invoiceList)}
                                    </div>
                                    <ListPaginationOptions
                                        pageCount={renderableData.pageCount}
                                        selectedPage={renderableData.selectedPage}
                                        urlBase="/subscription-invoices"
                                        location={props.location}
                                    />
                                </Fragment>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No subscription invoices found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
                {(() => {
                    if (renderableData.showUpdateInvoiceModal) {
                        return (
                            <SingleInvoice
                                cancel={closeModals}
                                invoiceId={renderableData.updateInvoiceId}
                                afterSuccessfulSave={afterSuccessfulInvoiceSave}
                                afterSuccessfulCancel={afterSuccessfulInvoiceCancel}
                                goToSubscription={goToReadSubscriptionModal}
                            />
                        );
                    } else if (renderableData.showConfirmSyncModal) {
                        return (
                            <ConfirmCancelModal
                                cancel={closeConfirmSyncModal}
                                confirm={performSync}
                                headerText="Confirm Sync"
                                confirmButtonText="SYNC"
                                warningMessage={[
                                    "Are you sure you want to sync all subscription invoices?",
                                    "This will sync any new Stripe subscription invoices from the last 30 days to our system and QBO."
                                ]}
                            />
                        )
                    }
                })()}
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
